import {
  deriveGstRegisteredFromDocuments,
  isGstDocument,
  resolveRegistrationDocumentType,
} from '../number-compliance/gst-documents'
import type {
  ComplianceFieldValue,
  ExistingDocument,
  RDocumentField,
} from '../number-compliance/types'
import type {
  BusinessVerificationStatus,
  RV2ComplianceApplication,
  RV2ComplianceDocument,
  RV2IndiaCaf,
  RV2IndiaCafDocument,
} from './types-v2'

// `window.open` only STARTS the navigation; the new tab fetches the URL after
// this frame. Revoking on the next statement can therefore invalidate a blob
// before the tab reads it, which renders a blank preview. Deferred instead, the
// same way `packages/shared/utils` defers its download revoke — longer here,
// because a new tab loading a PDF is slower than an anchor click consuming one.
const BLOB_REVOKE_DELAY_MS = 60_000

/**
 * Navigates to a document in a new tab, wrapping a `data:` URL as a blob first
 * and releasing it once the tab has had time to read it. Both callers are
 * download buttons: these URLs are served as octet-stream, so the tab saves the
 * file rather than rendering it.
 */
export const openDocumentInNewTab = (downloadUrl: string): void => {
  const targetUrl = getPreviewableDocumentUrl(downloadUrl)
  window.open(targetUrl, '_blank', 'noopener,noreferrer')
  if (targetUrl === downloadUrl) return
  setTimeout(() => URL.revokeObjectURL(targetUrl), BLOB_REVOKE_DELAY_MS)
}

// Browsers block opening data: URLs in a new tab, so any data: URL is
// re-wrapped as a blob URL at preview time; http(s) URLs pass through.
// Returns the original URL untouched when there is nothing safe to decode —
// callers are responsible for revoking the blob URL once they are done with it.
export const getPreviewableDocumentUrl = (url: string): string => {
  if (!url.startsWith('data:')) return url
  const [meta, base64] = url.split(',')
  const mimeMatch = meta.match(/data:(.*?);base64/)
  if (!mimeMatch || !base64) return url
  try {
    const bytes = Uint8Array.from(atob(base64), (char) => char.charCodeAt(0))
    return URL.createObjectURL(
      new Blob([bytes], { type: mimeMatch[1] || 'application/octet-stream' }),
    )
  } catch {
    return url
  }
}

// Matches documents by document_type_id (v1 matches by document_type_name).
export const mapV2ApplicationDocumentsToForm = (
  application: RV2ComplianceApplication,
  documentFields: RDocumentField[],
): {
  documentFieldValues: Record<string, Record<string, ComplianceFieldValue>>
  existingDocuments: Record<string, ExistingDocument>
} => {
  const documentFieldValues: Record<string, Record<string, ComplianceFieldValue>> = {}
  const existingDocuments: Record<string, ExistingDocument> = {}

  if (!application.documents) return { documentFieldValues, existingDocuments }

  for (const docField of documentFields) {
    const appDoc = application.documents.find(
      (d) => d.document_type_id === docField.document_type_id,
    )

    if (appDoc) {
      documentFieldValues[docField.document_type_id] = { ...appDoc.meta_information }

      existingDocuments[docField.document_type_id] = {
        v4_document_id: appDoc.document_id,
        file_name: appDoc.file_name,
        download_url: appDoc.download_url,
      }
    }
  }

  return { documentFieldValues, existingDocuments }
}

/**
 * The one document to show as the Business Document card on a saved India/GST
 * application, which stores TWO records (Registration Certificate and GST
 * certificate) holding the same uploaded file — see `selectSubmissionDocumentFields`
 * in `gst-documents.ts`. Picks the slot matching the answered branch: the GST
 * certificate when the application derives as GST-registered, the registration
 * slot otherwise. `documents.slice(0, 1)` would be wrong here — the registration
 * slot is submitted first regardless of which branch was taken, so slicing shows
 * the wrong document for a GST applicant.
 *
 * Returns every document untouched when there is nothing to disambiguate (0 or 1
 * stored documents, e.g. an application filed before the two-slot fan-out).
 */
export const selectDisplayDocuments = (
  documents: RV2ComplianceDocument[],
): RV2ComplianceDocument[] => {
  if (documents.length <= 1) return documents

  const registrationDocument = documents.find((doc) => !isGstDocument(doc.document_name))
  const registrationDocumentType = resolveRegistrationDocumentType(
    registrationDocument?.registration_document_type,
    registrationDocument?.meta_information,
  )
  const gstRegistered =
    deriveGstRegisteredFromDocuments(
      documents.map((doc) => doc.document_name),
      registrationDocumentType,
    ) ?? false
  const displayDocument = documents.find(
    (doc) => isGstDocument(doc.document_name) === gstRegistered,
  )

  return displayDocument ? [displayDocument] : documents
}

// The CAF's own document_type is the enum (gst|coi|udyam), not a display name.
// Null means the column was absent, which is different from "not GST" and must
// not collapse into it.
const cafDocumentTypeIsGst = (documentType?: string | null): boolean | null => {
  if (!documentType) return null
  return documentType.toLowerCase() === 'gst'
}

/**
 * The one supporting document to show on the Business Details card.
 *
 * A CAF stores TWO records holding the same uploaded file — a registration slot
 * and a GST slot — the same fan-out `selectDisplayDocuments` untangles for a
 * compliance application. That helper cannot be reused here: it keys off
 * `document_name`, `registration_document_type` and `meta_information`, none of
 * which the CAF payload carries.
 *
 * Picks the slot matching the answered branch: `gst_registered` is the direct
 * answer, and the CAF's `document_type` backfills it for records curated before
 * that column was whitelisted. Returns every document untouched when nothing
 * disambiguates — an unresolved branch should show too much rather than
 * confidently show the wrong document.
 */
export const selectCafDisplayDocuments = (caf: RV2IndiaCaf): RV2IndiaCafDocument[] => {
  const { documents } = caf
  if (documents.length <= 1) return documents

  const gstRegistered = caf.gst_registered ?? cafDocumentTypeIsGst(caf.document_type)
  if (gstRegistered === null) return documents

  // A document's own document_type is its display name ("GST Certificate"), so
  // the same GST test the compliance flow runs over `document_name` applies.
  const displayDocument = documents.find(
    (doc) => isGstDocument(doc.document_type ?? '') === gstRegistered,
  )

  return displayDocument ? [displayDocument] : documents
}

// number_service rejects any document_type outside gst|coi|udyam with a 400,
// so these three are the whole vocabulary. Anything else renders raw, so a
// future enum value shows up instead of disappearing.
const CAF_DOCUMENT_TYPES: Record<string, { code: string; name: string; numberLabel: string }> = {
  gst: { code: 'GST', name: 'GST Certificate', numberLabel: 'GSTIN' },
  coi: { code: 'COI', name: 'Certificate of Incorporation', numberLabel: 'CIN' },
  udyam: { code: 'Udyam', name: 'Udyam Certificate', numberLabel: 'Udyam number' },
}

const findCafDocumentType = (documentType?: string | null) =>
  documentType ? CAF_DOCUMENT_TYPES[documentType.toLowerCase()] : undefined

export const formatCafDocumentType = (documentType?: string | null): string => {
  if (!documentType) return '—'
  return findCafDocumentType(documentType)?.code ?? documentType
}

/** The full name, for where the bare code would read as jargon. */
export const formatCafDocumentName = (documentType?: string | null): string => {
  if (!documentType) return '—'
  return findCafDocumentType(documentType)?.name ?? documentType
}

/** What the number printed on the document is called. */
export const getCafDocumentNumberLabel = (documentType?: string | null): string =>
  findCafDocumentType(documentType)?.numberLabel ?? 'Document number'

/**
 * A document's `document_type` is the type's display name ("GST Certificate");
 * `file_name` is the stored file. The type leads and the file name sits under
 * it — the shape SavedBusinessDocumentCard already uses for the same document on
 * a compliance application, so one upload reads identically on both screens.
 * Falls back through the CAF's own document type to the file name, because
 * every CAF field is optional by construction and a row must never be headless.
 */
export const getCafDocumentLabel = (
  doc: RV2IndiaCafDocument,
  cafDocumentType?: string | null,
): string => {
  if (doc.document_type) return doc.document_type
  if (cafDocumentType) return findCafDocumentType(cafDocumentType)?.code ?? cafDocumentType
  // Typed required, but every CAF field is optional by construction upstream —
  // an empty one would render a blank row and an `aria-label` of "Preview ".
  return doc.file_name || '—'
}

// city/state/zipcode are free-standing fields on the CAF, any of which may be
// absent — filter+join twice (state/zip first, then the rest) so a missing
// part never leaves a stray comma or a dangling "–".
export const composeCafAddress = (caf: RV2IndiaCaf): string => {
  const stateZip = [caf.state, caf.zipcode].filter(Boolean).join(' – ')
  return [caf.registered_address_line1, caf.city, stateZip].filter(Boolean).join(', ')
}

/**
 * GSTIN line on the billing summary (Sagar, 09-11 #6a): a GST-registered CAF
 * shows its GSTIN, anything else reads "Unregistered".
 *
 * **number_service serialises neither `gst_registered` nor `gstin`**: both are
 * read-time Go methods on the `IndiaCAF` model with no JSON tag, derived from
 * `document_type`, so in practice both branches below collapse to the document
 * type. The field is still preferred first — same precedence
 * `selectCafDisplayDocuments` uses — so if either is ever serialised it wins
 * rather than being silently discarded.
 *
 * A GST document's number IS the GSTIN (number_service's own `GSTIN()` returns
 * `DocumentNumber` for a gst document), which is why `document_number` is an
 * exact fallback and not an approximation.
 *
 * Returns the label and the value together so the row cannot claim registered
 * while printing nothing. The same anticipation is already baked into
 * `selectCafDisplayDocuments`, which backfills `gst_registered` the same way.
 */
export const resolveCafGstin = (
  caf: RV2IndiaCaf,
): { isRegistered: boolean; gstin: string | null } => {
  const isRegistered = caf.gst_registered ?? cafDocumentTypeIsGst(caf.document_type) === true
  if (!isRegistered) return { isRegistered: false, gstin: null }
  return { isRegistered: true, gstin: caf.gstin || caf.document_number || null }
}

/**
 * Business verification `unverified`: the document on file was approved without
 * a seal and signature. The CAF update dialog says it.
 */
export const CAF_UNVERIFIED_HELP =
  "Your document on file isn't sealed and signed. Upload a copy stamped with your company seal and signed by an authorised signatory, so we can verify your company and its signatory."

/** What the CAF update dialog says in place of the Name field. */
export const getCafUpdateHelpText = (status?: BusinessVerificationStatus | null): string =>
  status === 'unverified'
    ? CAF_UNVERIFIED_HELP
    : "Only update your business details if your company's legal details have changed or no longer match your documents."
